import React, { useCallback, useEffect, useState } from "react";
import { Loader2, GripVertical } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

// Ordered by natural pipeline progression, not alphabetical.
const COLUMNS = [
    { key: "prospect", label: "Prospect", color: "border-slate-400" },
    { key: "invited", label: "Invited", color: "border-sky-400" },
    { key: "opened_email", label: "Opened", color: "border-indigo-400" },
    { key: "clicked", label: "Clicked", color: "border-violet-400" },
    { key: "passport_started", label: "Passport started", color: "border-fuchsia-400" },
    { key: "completed", label: "Completed", color: "border-emerald-400" },
    { key: "verification_pending", label: "Verif pending", color: "border-amber-400" },
    { key: "verified", label: "Verified", color: "border-emerald-500" },
    { key: "interviewing", label: "Interviewing", color: "border-sky-500" },
    { key: "placed", label: "Placed", color: "border-emerald-600" },
    { key: "archived", label: "Archived", color: "border-slate-300" },
];

export default function RecruitPipeline() {
    const [byStatus, setByStatus] = useState({});
    const [loading, setLoading] = useState(true);
    const [dragId, setDragId] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/recruit/crm/candidates?limit=500");
            const buckets = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
            (data.items || []).forEach((i) => {
                (buckets[i.status] || buckets.prospect).push(i);
            });
            setByStatus(buckets);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const move = async (invitationId, nextStatus) => {
        // Optimistic
        setByStatus((prev) => {
            const next = { ...prev };
            let card;
            for (const k of Object.keys(next)) {
                const idx = next[k].findIndex((x) => x.id === invitationId);
                if (idx >= 0) { card = { ...next[k][idx], status: nextStatus }; next[k] = next[k].filter((_, i) => i !== idx); break; }
            }
            if (card) next[nextStatus] = [card, ...(next[nextStatus] || [])];
            return next;
        });
        try {
            await api.patch(`/admin/recruit/crm/candidates/${invitationId}`, { status: nextStatus });
        } catch (err) {
            toast.error(formatApiError(err));
            load();
        }
    };

    if (loading) return <div className="py-16 flex justify-center"><Loader2 size={20} className="animate-spin text-violet-500" /></div>;

    const totalCards = Object.values(byStatus).reduce((n, arr) => n + arr.length, 0);

    return (
        <div data-testid="recruit-pipeline">
            <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                    <h2 className="font-heading font-medium text-lg">Pipeline</h2>
                    <p className="text-sm text-muted-foreground">Drag candidates across the 11 stages. Changes save automatically.</p>
                </div>
                <div className="text-[11px] text-muted-foreground shrink-0 hidden sm:block" data-testid="pipeline-scroll-hint">Scroll →</div>
            </div>
            {totalCards === 0 && (
                <div data-testid="pipeline-empty" className="mb-3 card-soft p-4 border-dashed text-sm text-muted-foreground">
                    No invitations yet. Send an invite from <span className="font-mono">Outreach</span> or add prospects from <span className="font-mono">Candidate Search</span> to populate the pipeline.
                </div>
            )}
            <div className="relative">
              <div className="flex gap-3 overflow-x-auto pb-4 -mx-1 px-1 scroll-smooth">
                {COLUMNS.map((col) => (
                    <div
                        key={col.key}
                        onDragOver={(e) => { e.preventDefault(); }}
                        onDrop={(e) => { e.preventDefault(); if (dragId) move(dragId, col.key); setDragId(null); }}
                        className={`w-64 shrink-0 border-t-4 ${col.color} bg-muted/20 rounded-md p-2`}
                        data-testid={`pipeline-col-${col.key}`}
                    >
                        <div className="flex items-center justify-between mb-2 px-1">
                            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{col.label}</div>
                            <div className="text-[11px] text-muted-foreground font-mono">{(byStatus[col.key] || []).length}</div>
                        </div>
                        <div className="space-y-2">
                            {(byStatus[col.key] || []).map((i) => (
                                <div
                                    key={i.id}
                                    draggable
                                    onDragStart={() => setDragId(i.id)}
                                    onDragEnd={() => setDragId(null)}
                                    data-testid={`pipeline-card-${i.id}`}
                                    className="card-soft p-2 cursor-grab active:cursor-grabbing bg-background hover:border-violet-500/40 transition"
                                >
                                    <div className="flex items-start gap-1.5">
                                        <GripVertical size={11} className="text-muted-foreground mt-0.5 shrink-0" />
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium truncate">{i.candidate_name || i.candidate_email}</div>
                                            <div className="text-[11px] text-muted-foreground truncate">{i.candidate_email}</div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {(byStatus[col.key] || []).length === 0 && (
                                <div className="text-[11px] text-muted-foreground text-center py-4 border border-dashed border-border rounded">Drop here</div>
                            )}
                        </div>
                    </div>
                ))}
              </div>
              {/* Right-edge fade to hint more columns exist */}
              <div className="pointer-events-none absolute top-0 right-0 h-full w-8 bg-gradient-to-l from-background to-transparent hidden sm:block" />
            </div>
        </div>
    );
}
